import React, { useEffect, useCallback } from 'react';
import AppleLogin from 'react-apple-login';
import { GoogleOAuthProvider, GoogleLogin } from '@react-oauth/google';

function decodeJwt(idToken) {
  try {
    const parts = idToken.split('.');
    if (parts.length !== 3) return null;

    const payloadB64 = parts[1];
    const normalized = payloadB64.replace(/-/g, '+').replace(/_/g, '/');
    const decoded = atob(normalized);
    const payload = JSON.parse(decoded);

    return {
      sub: payload.sub || null,
      email: payload.email || null
    };
  } catch (err) {
    console.error('Failed to decode JWT:', err);
    return null;
  }
}

export default function LoginScreen({ onLogin }) {
  const handleGoogleSuccess = useCallback((credentialResponse) => {
    console.log('Google login success:', credentialResponse);

    if (!credentialResponse?.credential) {
      alert('Não foi possível obter credenciais do Google');
      return;
    }

    // Decode the Google JWT credential
    const decoded = decodeJwt(credentialResponse.credential);
    if (!decoded || !decoded.sub) {
      alert('Não foi possível identificar usuário do Google');
      return;
    }

    // Format userId as google:${sub}
    const userId = `google:${decoded.sub}`;
    const userEmail = decoded.email || null;

    // Google JWT includes name in 'name' field
    let userName = null;
    if (decoded.sub) {
      // Decode again to get full payload including name
      try {
        const parts = credentialResponse.credential.split('.');
        const payloadB64 = parts[1];
        const normalized = payloadB64.replace(/-/g, '+').replace(/_/g, '/');
        const fullPayload = JSON.parse(atob(normalized));
        userName = fullPayload.name || null;
        console.log('Google full payload:', fullPayload);
      } catch (err) {
        console.error('Error decoding full Google payload:', err);
      }
    }

    console.log('Google login - userId:', userId, 'name:', userName, 'email:', userEmail);

    if (userName || userEmail) {
      onLogin(userId, { name: userName, email: userEmail });
    } else {
      onLogin(userId);
    }
  }, [onLogin]);

  const handleGoogleError = useCallback(() => {
    console.error('Google login failed');
    alert('Falha ao fazer login com Google. Tente novamente.');
  }, []);

  const handleAppleSuccess = useCallback((data) => {
    console.log('Apple login success:', data);

    let derivedId = null;
    let userName = null;
    let userEmail = null;

    if (data?.authorization?.id_token) {
      const decoded = decodeJwt(data.authorization.id_token);
      if (decoded) {
        derivedId = decoded.sub;
        userEmail = decoded.email;
        console.log('Extracted userId from JWT sub:', derivedId);
        console.log('Extracted email from JWT:', userEmail);
      }
    }

    if (!derivedId && data?.user?.email) {
      derivedId = data.user.email.toLowerCase();
      console.log('Using email as userId:', derivedId);
    }

    if (!derivedId) {
      alert('Não foi possível identificar usuário');
      return;
    }

    if (data?.user?.name) {
      const { firstName, lastName } = data.user.name;
      userName = [firstName, lastName].filter(Boolean).join(' ');
      console.log('Extracted userName:', userName, 'from', data.user.name);
    } else {
      console.log('No name in Apple response - data.user:', data?.user);
    }

    console.log('About to call onLogin with:');
    console.log('  - derivedId:', derivedId);
    console.log('  - userName:', userName);
    console.log('  - userEmail:', userEmail);

    if (userName || userEmail) {
      console.log('Calling onLogin WITH metadata:', { name: userName, email: userEmail });
      onLogin(derivedId, { name: userName, email: userEmail });
    } else {
      console.log('Calling onLogin WITHOUT metadata');
      onLogin(derivedId);
    }
  }, [onLogin]);

  const handleError = useCallback((err) => {
    console.error('Apple login error:', err);
    alert('Falha ao fazer login com Apple. Tente novamente.');
  }, []);

  useEffect(() => {
    const clientId = process.env.REACT_APP_APPLE_CLIENT_ID;
    const redirectURI = process.env.REACT_APP_APPLE_REDIRECT_URI || window.location.origin;

    if (!clientId) {
      console.error('REACT_APP_APPLE_CLIENT_ID not set');
      return;
    }

    if (window.AppleID) {
      window.AppleID.auth.init({
        clientId,
        scope: 'name email',
        redirectURI,
        state: 'origin:web',
        usePopup: true
      });

      const onSuccess = (event) => {
        console.log('AppleIDSignInOnSuccess event:', event.detail);
        handleAppleSuccess(event.detail);
      };

      const onFailure = (event) => {
        console.error('AppleIDSignInOnFailure event:', event.detail);
        handleError(event.detail.error);
      };

      document.addEventListener('AppleIDSignInOnSuccess', onSuccess);
      document.addEventListener('AppleIDSignInOnFailure', onFailure);

      return () => {
        document.removeEventListener('AppleIDSignInOnSuccess', onSuccess);
        document.removeEventListener('AppleIDSignInOnFailure', onFailure);
      };
    }
  }, [handleAppleSuccess, handleError]);

  const handleDevLogin = () => {
    const devUserId = process.env.REACT_APP_DEV_USER_ID;
    if (devUserId) {
      onLogin(devUserId, { name: 'Dev User', email: 'dev@bhz.bar' });
    }
  };

  const googleClientId = process.env.REACT_APP_GOOGLE_CLIENT_ID;

  // Debug logging
  console.log('Google Client ID:', googleClientId);
  console.log('Current window.location.origin:', window.location.origin);

  return (
    <GoogleOAuthProvider clientId={googleClientId}>
      <div className="login-page">
        <div style={{
          maxWidth: 400,
          width: '100%',
          textAlign: 'center'
        }}>
          <h2 style={{
            marginBottom: '0.5rem',
            fontSize: '1.5rem',
            color: '#333',
            fontWeight: 600
          }}>
            Faça login
          </h2>
          <p style={{
            margin: '0 0 2rem',
            fontSize: '0.9rem',
            color: '#666'
          }}>
            Para sugerir bares e comentar.
          </p>

          {/* Google Sign-In Button */}
          <div style={{
            display: 'flex',
            justifyContent: 'center',
            marginBottom: '12px'
          }}>
            <GoogleLogin
              onSuccess={handleGoogleSuccess}
              onError={handleGoogleError}
              type="standard"
              theme="outline"
              size="large"
              text="signin_with"
              shape="rectangular"
              logo_alignment="left"
              width="280"
              locale="pt-BR"
            />
          </div>

          {/* Apple Sign-In Button */}
          <div style={{
            display: 'inline-block',
            width: '280px',
            height: '44px',
            overflow: 'hidden',
            margin: '0 auto'
          }}>
            <div id="appleid-signin" style={{ width: '280px', height: '44px' }}>
              <AppleLogin
                clientId={process.env.REACT_APP_APPLE_CLIENT_ID}
                redirectURI={process.env.REACT_APP_APPLE_REDIRECT_URI || window.location.origin}
                responseType="code id_token"
                responseMode="fragment"
                scope="name email"
                usePopup={true}
                callback={handleAppleSuccess}
                onError={handleError}
                designProp={{
                  height: 44,
                  width: 280,
                  color: 'black',
                  border_radius: 12,
                  type: 'sign-in',
                  locale: 'pt_BR'
                }}
              />
            </div>
          </div>

          {process.env.REACT_APP_DEV_USER_ID && (
            <button
              onClick={handleDevLogin}
              style={{
                width: '280px',
                height: 40,
                border: '1px dashed #ccc',
                borderRadius: 8,
                background: '#f9f9f9',
                fontSize: '13px',
                cursor: 'pointer',
                color: '#666',
                marginTop: '16px',
                fontWeight: 500
              }}
            >
              🔧 DEV: Login rápido
            </button>
          )}
        </div>
      </div>
    </GoogleOAuthProvider>
  );
}
